import { useEffect, useRef, useState, useMemo } from "react";
import {
	APP_CONFIG,
	DropdownStyle,
	formatDate,
} from "../../../../helpers/appHelpers";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { Popover } from "@mui/material";
import { IoCaretDown } from "react-icons/io5";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import { useManageTableWithSearchParams } from "../../../../helpers/useManageTable";
import { Payslips, QueryPayrollDto } from "../../../../models/payroll.dto";
import {
	useGetCompanyPayslips,
	useGetCompanyPayslipsSearch,
	useGetEmployeePayslipPdf,
} from "../../../../services/payroll.service";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import { saveAs } from "file-saver";
import { BsDownload } from "react-icons/bs";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import APISearchInput from "../../../../components/search/APISearchInput";
import {
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import {
	companyPayslipsColumns,
	companyPayslipsColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";

// Column definitions for the Excel export.
// Matches companyPayslipsColumns — update here if columns change.
const EXCEL_COLUMNS = [
	{ header: "Employee", key: "employee", numeric: false },
	{ header: "Basic Pay", key: "basicPayAmount", numeric: true },
	{ header: "Gross Pay", key: "grossAmount", numeric: true },
	{ header: "Net Pay", key: "netPayAmount", numeric: true },
	{
		header: "Employee Pension Contribution",
		key: "employeePensionAmount",
		numeric: true,
	},
	{
		header: "Employer Pension Contribution",
		key: "employerPensionAmount",
		numeric: true,
	},
	{ header: "Income Tax", key: "taxAmount", numeric: true },
	{
		header: "Employee Saving Scheme Amount",
		key: "employeeSavingSchemeAmount",
		numeric: true,
	},
	{
		header: "Employer Saving Scheme Amount",
		key: "employerSavingSchemeAmount",
		numeric: true,
	},
	{ header: "Allowance Amount", key: "allowanceAmount", numeric: true },
	{ header: "Overtime Amount", key: "overtimeAmount", numeric: true },
	{ header: "Bonus Amount", key: "bonusAmount", numeric: true },
	{ header: "Backpay Amount", key: "backpayAmount", numeric: true },
	{
		header: "Loan Repayment Amount",
		key: "loanRepaymentAmount",
		numeric: true,
	},
	{
		header: "Partial Salary Amount",
		key: "partialSalaryAmount",
		numeric: true,
	},
	{
		header: "Salary Advance Amount",
		key: "salaryAdvanceAmount",
		numeric: true,
	},
	{
		header: "Union Deductions Amount",
		key: "unionDeductionsAmount",
		numeric: true,
	},
] as const;

const CompanyPayslips = ({ defaultPayPeriod }: { defaultPayPeriod: any }) => {
	const { currentCompany } = useCompanyStore();
	const { currentUser, userType } = useUserStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [payPeriodAnchorEl, setPayPeriodAnchorEl] =
		useState<HTMLButtonElement | null>(null);
	const [activePayPeriod, setActivePayPeriod] = useState<PayPeriodDto>();
	const [downloadingFile, setDownloadingFile] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(companyPayslipsColumnVisibility);

	useEffect(() => {
		if (!defaultPayPeriod || activePayPeriod) return;
		setActivePayPeriod(defaultPayPeriod as any);
	}, [defaultPayPeriod]);

	const { mutate: searchCompanyPayslips } = useGetCompanyPayslipsSearch(
		currentCompany?.id as number,
	);
	const {
		fetchedTableData,
		tableAPIRef,
		tableData,
		isLoading,
		pageCount,
		searching,
		setTableData,
	} = useManageTableWithSearchParams<Payslips>({
		fetchHook: (options: QueryPayrollDto) =>
			useGetCompanyPayslips(
				currentCompany?.id as number,
				(activePayPeriod?.id as number) || defaultPayPeriod?.id,
				options,
			),
		options: { orderBy: undefined },
		reloadDeps: [activePayPeriod],
	});

	const { data: payPeriods, isLoading: loadingPayPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});

	const { mutate: fetchPayslipPdf, isLoading: fetchingPayslipPdf } =
		useGetEmployeePayslipPdf();

	// Generate the Excel client-side using SheetJS so we can control:
	// - Number formatting (always 2 decimal places via cell format "0.00")
	// - A TOTAL row appended at the bottom summing all numeric columns
	const handleDownload = () => {
		if (!tableData.length) return;
		setDownloadingFile(true);

		try {
			const payPeriod = activePayPeriod || defaultPayPeriod;

			// ── Data rows ────────────────────────────────────────────────────
			const dataRows = tableData.map((row: any) => {
				const out: Record<string, string | number> = {};
				EXCEL_COLUMNS.forEach((col) => {
					if (col.key === "employee") {
						const emp = row.employee;
						out[col.header] = emp
							? `${emp.firstName} ${emp.lastName}`.trim()
							: "";
					} else {
						out[col.header] = Number(row[col.key] ?? 0);
					}
				});
				return out;
			});

			// ── Totals row ───────────────────────────────────────────────────
			const totalsRow: Record<string, string | number> = { Employee: "TOTAL" };
			EXCEL_COLUMNS.filter((c) => c.numeric).forEach((col) => {
				totalsRow[col.header] = tableData.reduce(
					(sum: number, row: any) => sum + Number(row[col.key] ?? 0),
					0,
				);
			});
			dataRows.push(totalsRow);

			// ── Build worksheet ──────────────────────────────────────────────
			const ws = XLSX.utils.json_to_sheet(dataRows, {
				header: EXCEL_COLUMNS.map((c) => c.header),
			});

			// Apply "0.00" number format to every numeric cell (data + totals)
			const range = XLSX.utils.decode_range(ws["!ref"]!);
			for (let R = 1; R <= range.e.r; R++) {
				EXCEL_COLUMNS.forEach((col, C) => {
					if (!col.numeric) return;
					const addr = XLSX.utils.encode_cell({ r: R, c: C });
					if (ws[addr]) {
						ws[addr].t = "n";
						ws[addr].z = "0.00";
					}
				});
			}

			// Auto-size columns
			ws["!cols"] = EXCEL_COLUMNS.map((col) => ({
				wch: Math.max(col.header.length + 4, 16),
			}));

			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "Payslips");

			const filename = `payroll_${payPeriod.code}(${formatDate(
				payPeriod.startDate,
			)} - ${formatDate(payPeriod.endDate)}).xlsx`;
			XLSX.writeFile(wb, filename);
		} finally {
			setDownloadingFile(false);
		}
	};

	// ✅ Actions column for TanStack Table
	const actionsColumn = useMemo(
		() => ({
			id: "actions",
			header: "Actions",
			size: 180,
			cell: ({ row }) => {
				const employee = row.original.employee;
				return (
					<button
						disabled={fetchingPayslipPdf}
						className="py-2 px-4 rounded-[6px] flex items-center gap-3 text-primary-blue text-2sm hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
						onClick={(e) => {
							e.stopPropagation();

							if (!employee?.id || !activePayPeriod?.id) {
								toast.error("Missing employee or pay period ID");
								return;
							}

							fetchPayslipPdf(
								{
									employeeId: employee.id,
									payPeriodId: activePayPeriod.id,
								},
								{
									onSuccess: (data) => {
										if (data.data) {
											const fileName =
												"payslip_" +
												activePayPeriod.code +
												`(${employee.firstName} ${employee.lastName})` +
												".pdf";
											saveAs(data.data, fileName);
										} else {
											toast.error("Payslip PDF not found");
										}
									},
								},
							);
						}}
					>
						<BsDownload className="font-bold" />
						<p>Download</p>
					</button>
				);
			},
		}),
		[fetchingPayslipPdf, activePayPeriod, fetchPayslipPdf],
	);

	const columnsWithActions = useMemo(
		() => [...companyPayslipsColumns, actionsColumn],
		[actionsColumn],
	);

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<>
						<button
							onClick={(e: any) => setPayPeriodAnchorEl(e.currentTarget)}
							className="relative border border-gray-300 rounded-lg text-2xs py-3 px-3 flex items-center gap-3"
						>
							<div className="absolute top-0 -translate-y-1/2 left-2 px-2 bg-white text-[11px]">
								Pay Period
							</div>
							{activePayPeriod?.id ? (
								<span className="max-w-full truncate">
									{activePayPeriod.code + " "}
									<span className="text-xs">
										({formatDate(activePayPeriod.startDate)} -{" "}
										{formatDate(activePayPeriod.endDate)})
									</span>
								</span>
							) : (
								<span className="pr-8">Select Option</span>
							)}
							<IoCaretDown />
						</button>
						<Popover
							open={Boolean(payPeriodAnchorEl)}
							anchorEl={payPeriodAnchorEl}
							onClose={() => setPayPeriodAnchorEl(null)}
							anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
							transformOrigin={{ vertical: "top", horizontal: "left" }}
							PaperProps={{ style: { ...DropdownStyle } }}
						>
							<div className="py-1 flex flex-col">
								{loadingPayPeriods ? <p>Loading...</p> : null}
								{!payPeriods?.data.length && !loadingPayPeriods ? (
									<p>No Pay Periods found</p>
								) : null}
								{payPeriods?.data.map((payPeriod) => (
									<button
										key={payPeriod.id}
										className={`w-full pl-4 pr-7 py-2 text-start text-gray-700
                                            ${
																							activePayPeriod?.id ===
																							payPeriod.id
																								? "bg-gray-200"
																								: "hover:bg-gray-100"
																						}
                                        `}
										role="menuitem"
										onClick={() => {
											setActivePayPeriod(payPeriod);
											setPayPeriodAnchorEl(null);
										}}
									>
										{payPeriod.code + " "}
										<span className="text-xs">
											({formatDate(payPeriod.startDate)} -{" "}
											{formatDate(payPeriod.endDate)})
										</span>
									</button>
								))}
							</div>
						</Popover>
					</>
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchCompanyPayslips}
					/>
					<TableFilter fields={[]} />
					<ManageTableColumns
						columns={columnsWithActions}
						defaultVisibilityModel={companyPayslipsColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						minVisibleColumns={7}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<OrangeButton
					title={downloadingFile ? "Downloading..." : "Download Excel File"}
					className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
					onClick={handleDownload}
					disabled={
						downloadingFile ||
						!tableData.length ||
						isLoading ||
						Boolean(searching)
					}
				/>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.PAYSLIPS}
				data={tableData}
				columns={columnsWithActions}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				initialColumnVisibility={companyPayslipsColumnVisibility}
				pageSize={10}
				hasPermission={checkUserPermissionsAnd([PERMISSIONS.PAYSLIPS_READ])}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
		</>
	);
};

export default CompanyPayslips;
